<template>
  <Sheet>
    <template #leading>
      <Sheet.Button
        :icon="isRoot ? CloseIcon : ChevronLeftIcon"
        @click="handleBack"
      />
    </template>
    <template #title>{{ currentFolder.name }}</template>
    <template #trailing>
      <Sheet.Button
        variant="primary"
        :icon="CheckmarkIcon"
        @click="handleSelect"
      />
    </template>

    <div class="relative flex min-h-0 flex-1 flex-col">
      <Sheet.Body class="px-0!">
        <div
          v-if="loading"
          class="flex h-full items-center justify-center py-10"
        >
          <Spinner :size="20" />
        </div>

        <template v-else-if="folders.length">
          <template v-for="(folder, index) in folders" :key="folder.id">
            <button
              type="button"
              class="flex h-16 w-full items-center gap-4 px-4 text-left"
              @click="enter(folder)"
            >
              <div class="w-10 shrink-0">
                <Thumbnail type="folder" />
              </div>
              <p
                class="truncate font-secondary text-[16px] leading-[20px] text-gray-800"
              >
                {{ folder.name }}
              </p>
            </button>
            <div
              v-if="index < folders.length - 1"
              class="ml-[72px] mr-4 h-px bg-gray-800/10"
            />
          </template>
        </template>

        <div
          v-else
          class="flex h-full items-center justify-center py-10 font-secondary text-[14px] text-gray-500"
        >
          Select {{ currentFolder.name }}
        </div>
      </Sheet.Body>

      <button
        type="button"
        class="absolute bottom-4 left-4 flex size-[50px] items-center justify-center rounded-full bg-white text-gray-800 shadow-[0_4px_18px_0_rgba(0,0,0,0.12)]"
        @click="createFolder"
      >
        <FolderAddIcon class="size-6" />
      </button>
    </div>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Sheet, Thumbnail, useSheet } from '@dacr/library'
import {
  FolderAddIcon,
  CloseIcon,
  ChevronLeftIcon,
  CheckmarkIcon,
} from '@dacr/icons'
import Spinner from '@/shared/components/Spinner.vue'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useUploadStore } from '@/features/upload/store'
import { useFoldersQuery } from '@/shared/graphql/legacy/generated'

const { close } = useSheet()
const uploadStore = useUploadStore()

const path = ref([{ id: 'root', name: 'Dacr', path: '' }])

const currentFolder = computed(() => path.value[path.value.length - 1])
const isRoot = computed(() => path.value.length === 1)

const variables = computed(() => ({ id: currentFolder.value.id }))

const { result, loading } = useFoldersQuery(variables, {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-only',
})

const folders = computed(() => result.value?.folders.items ?? [])

const enter = (folder: { id: string; name: string }) => {
  path.value.push({
    id: folder.id,
    name: folder.name,
    path: `${currentFolder.value.path}/${folder.name}`,
  })
}

const handleBack = () => {
  if (isRoot.value) {
    close()
  } else {
    path.value.pop()
  }
}

const handleSelect = () => {
  uploadStore.updateDraft({
    folderId: currentFolder.value.id,
    folderName: isRoot.value ? undefined : currentFolder.value.name,
  })
  close()
}

const createFolder = () => {
  openDialog(Dialog.AddFolder, {
    stack: true,
    props: {
      folderId: currentFolder.value.id,
      parentPath: currentFolder.value.path,
      handler(folder: { id: string; name: string }) {
        enter(folder)
      },
    },
  })
}
</script>
